import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const springEasing = [0.22, 1, 0.36, 1];

const officePhotos = [
{
  id: 1,
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1d1342fbb-1766936728417.png",
  alt: 'Modern open-plan office with standing desks, large monitors, and floor-to-ceiling windows overlooking Oslo cityscape',
  caption: 'Vårt moderne kontor i Oslo sentrum',
  category: 'office'
},
{
  id: 2,
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1e9d7d421-1766878835739.png",
  alt: 'Bright collaborative workspace with white walls, wooden furniture, and team members working at shared tables',
  caption: 'Åpent samarbeidsrom for kreative prosesser',
  category: 'office'
},
{
  id: 3,
  src: "https://images.unsplash.com/photo-1560427621-d60cca6f9ffd",
  alt: 'Sleek reception area with company logo on wall, minimalist furniture, and natural lighting',
  caption: 'Resepsjonsområde med nordisk design',
  category: 'office'
},
{
  id: 4,
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_13040bda0-1772199582896.png",
  alt: 'Private meeting room with glass walls, conference table, and presentation screen showing data analytics',
  caption: 'Møterom med avansert presentasjonsutstyr',
  category: 'office'
}];


const teamPhotos = [
{
  id: 1,
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_126eac5ca-1766838007534.png",
  alt: 'Diverse team of five professionals gathered around a laptop, pointing at screen and smiling during a brainstorming session',
  caption: 'Teamet i full gang med produktutvikling',
  category: 'team'
},
{
  id: 2,
  src: "https://images.unsplash.com/photo-1675715310072-a4eb052d1341",
  alt: 'Two colleagues standing at a whiteboard covered in sticky notes and diagrams, discussing project roadmap',
  caption: 'Strategiplanlegging og roadmap-utvikling',
  category: 'team'
},
{
  id: 3,
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1dfbc27cd-1766500363073.png",
  alt: 'Team of engineers and developers in a huddle reviewing code on multiple monitors in a tech office environment',
  caption: 'Teknisk team i kodegjennomgang',
  category: 'team'
},
{
  id: 4,
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1b2b753a1-1772199580773.png",
  alt: 'Business meeting with four professionals around a round table, reviewing printed reports and discussing results',
  caption: 'Kvartalsmøte med ledelsen',
  category: 'team'
}];


const vehiclePhotos = [
{
  id: 1,
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_191a42cb6-1772199582906.png",
  alt: 'White company van with Veiassistent logo parked on Norwegian highway with mountains in background',
  caption: 'Veiassistent servicebil — klar for veien',
  category: 'vehicle'
},
{
  id: 2,
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1512018d6-1772199579025.png",
  alt: 'Fleet of three white company cars lined up in parking lot, showing consistent branding across vehicles',
  caption: 'Vår flåte av servicekjøretøy',
  category: 'vehicle'
},
{
  id: 3,
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_17b90ee56-1772191894544.png",
  alt: 'Close-up of company vehicle dashboard with GPS navigation system showing Norwegian road network and toll stations',
  caption: 'Avansert GPS og bompengesystem installert',
  category: 'vehicle'
},
{
  id: 4,
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_19ebcf5db-1772199581435.png",
  alt: 'Electric company vehicle charging at a charging station with Norwegian fjord landscape visible in background',
  caption: 'Elektrisk kjøretøy — bærekraftig transport',
  category: 'vehicle'
}];


const locationPhotos = [
{
  id: 1,
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_10dc90f96-1772199579831.png",
  alt: 'Aerial view of Oslo city center with Oslofjord in background, showing the urban landscape where Veiassistent is headquartered',
  caption: 'Oslo — vår hjemby og hovedkontor',
  category: 'location'
},
{
  id: 2,
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_18ccfa408-1767912882103.png",
  alt: 'Modern glass office building exterior with company signage, surrounded by trees and a paved entrance',
  caption: 'Kontorbygget i Bjørvika, Oslo',
  category: 'location'
},
{
  id: 3,
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_11b3ee6f9-1772199581776.png",
  alt: 'Norwegian highway with toll station visible in distance, surrounded by pine forest and clear blue sky',
  caption: 'Norske veier — vårt arbeidsområde',
  category: 'location'
},
{
  id: 4,
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1310cb2ab-1772199580893.png",
  alt: 'Bergen office location with colorful Bryggen wharf buildings and mountain backdrop visible from office windows',
  caption: 'Avdelingskontor i Bergen',
  category: 'location'
}];


const GallerySection = ({ title, subtitle, accentLabel, photos, accentColor = '#FF5722' }) => {
  const [lightbox, setLightbox] = useState(null);

  return (
    <section style={{ padding: '80px 24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Section header */}
      <div style={{ marginBottom: '48px', display: 'flex', alignItems: 'flex-start', gap: '24px' }}>
        <div style={{ flexShrink: 0, marginTop: '6px' }}>
          <div style={{ width: '3px', height: '48px', background: accentColor, borderRadius: '2px' }} />
        </div>
        <div>
          <span style={{ fontFamily: 'var(--font-heading)', fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: accentColor, display: 'block', marginBottom: '8px' }}>{accentLabel}</span>
          <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '32px', fontWeight: 700, color: 'var(--color-foreground)', marginBottom: '12px', lineHeight: 1.2 }}>{title}</h2>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: '16px', color: 'var(--color-muted-foreground)', maxWidth: '560px', lineHeight: 1.6 }}>{subtitle}</p>
        </div>
      </div>
      {/* Photo grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '16px' }}>
        {photos?.map((photo, i) =>
        <motion.div
          key={photo?.id}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: i * 0.08, duration: 0.5, ease: springEasing }}
          onClick={() => setLightbox(photo)}
          style={{ position: 'relative', overflow: 'hidden', borderRadius: '2px', cursor: 'pointer', border: '1px solid rgba(224,228,232,0.08)', aspectRatio: '4/3' }}
          whileHover={{ scale: 1.02 }}>
          
            <img
            src={photo?.src}
            alt={photo?.alt}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.4s ease' }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.06)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'} />
          
            {/* Overlay */}
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(10,12,16,0.85) 0%, transparent 50%)', opacity: 0, transition: 'opacity 0.3s ease' }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '0'}>
            
              <div style={{ position: 'absolute', bottom: '16px', left: '16px', right: '16px' }}>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: 500, color: '#FFFFFF', lineHeight: 1.4 }}>{photo?.caption}</p>
              </div>
            </div>
            {/* Corner accent */}
            <div style={{ position: 'absolute', top: '12px', right: '12px', width: '24px', height: '24px', border: `2px solid ${accentColor}`, borderRadius: '2px', opacity: 0.7 }} />
          </motion.div>
        )}
      </div>
      {/* Lightbox */}
      <AnimatePresence>
        {lightbox &&
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setLightbox(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          
            <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.3, ease: springEasing }}
            onClick={(e) => e?.stopPropagation()}
            style={{ position: 'relative', maxWidth: '900px', width: '100%', borderRadius: '2px', overflow: 'hidden', border: '1px solid rgba(224,228,232,0.12)' }}>
            
              <img src={lightbox?.src} alt={lightbox?.alt} style={{ width: '100%', display: 'block', maxHeight: '70vh', objectFit: 'cover' }} />
              <div style={{ background: 'rgba(26,29,32,0.98)', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: 500, color: 'var(--color-foreground)' }}>{lightbox?.caption}</p>
                <button onClick={() => setLightbox(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-muted-foreground)', fontSize: '20px', lineHeight: 1 }}>✕</button>
              </div>
            </motion.div>
          </motion.div>
        }
      </AnimatePresence>
    </section>);

};

const OfficeGallery = () => {
  return (
    <div>
      {/* Divider */}
      <div style={{ maxWidth: '1200px', margin: '0 auto 0', padding: '0 24px' }}>
        <div style={{ height: '1px', background: 'linear-gradient(to right, transparent, rgba(255,87,34,0.3), transparent)' }} />
      </div>

      <GallerySection
        accentLabel="Kontorlokaler"
        title="Vårt arbeidsmiljø"
        subtitle="Et moderne og inspirerende kontormiljø som fremmer kreativitet, samarbeid og innovasjon i hjertet av Oslo."
        photos={officePhotos}
        accentColor="#FF5722" />
      

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
        <div style={{ height: '1px', background: 'linear-gradient(to right, transparent, rgba(0,79,152,0.3), transparent)' }} />
      </div>

      <GallerySection
        accentLabel="Teamsamarbeid"
        title="Menneskene bak Veiassistent"
        subtitle="Vårt dedikerte team jobber tett sammen for å levere de beste løsningene for norske sjåfører og transportselskaper."
        photos={teamPhotos}
        accentColor="#004F98" />
      

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
        <div style={{ height: '1px', background: 'linear-gradient(to right, transparent, rgba(255,87,34,0.3), transparent)' }} />
      </div>

      <GallerySection
        accentLabel="Kjøretøyflåte"
        title="Vår flåte av servicekjøretøy"
        subtitle="Moderne og miljøvennlige kjøretøy utstyrt med det nyeste innen GPS-teknologi og bompengesystemer for effektiv service."
        photos={vehiclePhotos}
        accentColor="#FF5722" />
      

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
        <div style={{ height: '1px', background: 'linear-gradient(to right, transparent, rgba(0,79,152,0.3), transparent)' }} />
      </div>

      <GallerySection
        accentLabel="Kontorlokasjoner"
        title="Hvor du finner oss"
        subtitle="Med kontorer i Oslo og Bergen er vi strategisk plassert for å betjene kunder over hele Norge, fra sør til nord."
        photos={locationPhotos}
        accentColor="#004F98" />
      
    </div>);

};

export default OfficeGallery;